import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "@phosphor-icons/react";
import CampaignFormFields from "@/components/CampaignFormFields";
import PredictiveCampaignFields from "@/components/PredictiveCampaignFields";
import AiAgentCampaignFields from "@/components/AiAgentCampaignFields";
import useCampaignFormOptions from "@/hooks/useCampaignFormOptions";
import {
  hydrateFromCampaign,
  validateCampaignForm,
  buildUpdateCampaignBody,
  dialingTypeLabel,
} from "@/lib/campaignForm";

/** Full-page campaign editor. Predictive / Manual / AI Agent use dedicated layouts. */
export default function CampaignEdit() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { options, optionsError, pdServers, loading: optionsLoading, reload: reloadFormOptions } = useCampaignFormOptions(true);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vmUploading, setVmUploading] = useState(false);
  const vmInputRef = useRef(null);
  const patch = (p) => setForm((prev) => (prev ? { ...prev, ...p } : prev));

  const back = useCallback(() => navigate("/campaigns"), [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/campaigns/${campaignId}`);
        if (!cancelled) setForm(hydrateFromCampaign(data));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load campaign");
        if (!cancelled) navigate("/campaigns", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, navigate]);

  const save = async () => {
    const check = validateCampaignForm(form, { pdServerCount: pdServers.length });
    if (!check.ok) { toast.error(check.error); return; }
    setSaving(true);
    try {
      await api.put(`/campaigns/${form.id}`, buildUpdateCampaignBody(form, { pdServers }));
      toast.success("Campaign updated");
      back();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadVoicemail = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setVmUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/audio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch({ vmAudioId: data.id, vmFilename: data.original_filename });
      if (data.warning) toast.warning(data.warning);
      else toast.success("Voicemail audio uploaded (telephony-compatible)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setVmUploading(false); e.target.value = ""; }
  };

  // Wait for the option lists too: rendering early shows "no user groups / no
  // lists yet" next to selections the campaign actually has.
  if (loading || optionsLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!form) return null;

  const isPredictive = form.dialingType === "predictive";
  const isManual = form.dialingType === "manual";
  const isAiAgent = form.dialingType === "bot";
  const useAgentLayout = isPredictive || isManual;
  const editTitle = isPredictive
    ? "Predictive"
    : isManual
      ? "Manual"
      : isAiAgent
        ? "AI Agent"
        : "campaign";

  return (
    <div data-testid="campaign-edit-page">
      <PageHeader
        title={`Edit ${editTitle} — ${form.name || ""}`}
        subtitle={
          isAiAgent
            ? "Campaign information, leads, dialing times, routing, and AMD."
            : useAgentLayout
              ? (isManual
                ? "Campaign information, leads, schedule, and routing."
                : "Campaign information, leads, schedule, routing, and AMD.")
              : `Dialing type: ${dialingTypeLabel(form.dialingType)}. The routing prefix stays the same.`
        }
      >
        <Button variant="outline" className="rounded-lg" onClick={back} data-testid="campaign-edit-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" weight="bold" /> Back to campaigns
        </Button>
      </PageHeader>

      <div className="max-w-4xl">
        {isAiAgent ? (
          <AiAgentCampaignFields
            mode="edit"
            value={form}
            onChange={patch}
            options={options}
            optionsError={optionsError}
            vmUploading={vmUploading}
            vmInputRef={vmInputRef}
            onVmUploadClick={() => vmInputRef.current?.click()}
            onVmFileChange={uploadVoicemail}
          />
        ) : useAgentLayout ? (
          <PredictiveCampaignFields
            mode="edit"
            variant={isManual ? "manual" : "predictive"}
            value={form}
            onChange={patch}
            options={options}
            optionsError={optionsError}
            onInboundGroupsReload={reloadFormOptions}
            vmUploading={vmUploading}
            vmInputRef={vmInputRef}
            onVmUploadClick={() => vmInputRef.current?.click()}
            onVmFileChange={uploadVoicemail}
            showSchedule
          />
        ) : (
          <CampaignFormFields
            mode="edit"
            value={form}
            onChange={patch}
            options={options}
            optionsError={optionsError}
            vmUploading={vmUploading}
            vmInputRef={vmInputRef}
            onVmUploadClick={() => vmInputRef.current?.click()}
            onVmFileChange={uploadVoicemail}
            onInboundGroupsReload={reloadFormOptions}
          />
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" className="rounded-lg" onClick={back} data-testid="cancel-edit-campaign-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="submit-edit-campaign-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
